// Libs
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  UserGroupIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";

// Local
import { ExistingCustomer } from "../../models/customer";
import { ExistingEmailCampaign } from "../../models/email-campaign";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";
import { EMAIL_CAMPAIGN_LIMITS } from "../../constants/email";
import {
  queryCustomersWithFilters,
  getCustomerCountWithFilters,
  getCustomerIdsFromCampaigns,
  getAvailableFilterOptions,
} from "../../database/customer-queries";

/**
 * Filter configuration for customer queries
 */
export interface CustomerFilter {
  type: "customerType" | "hasEmail" | "excludeCampaigns" | "tags" | "dateRange";
  value: any;
  label: string;
}

/**
 * Customer segment with filters and results
 */
export interface CustomerSegment {
  id: string;
  name: string;
  filters: CustomerFilter[];
  customerCount: number;
  customers: ExistingCustomer[];
  isExpanded: boolean;
}

/**
 * Props for the SmartCustomerSelector component
 */
interface SmartCustomerSelectorProps {
  siteKey: string;
  selectedCustomerIds: string[];
  onSelectionChange: (customerIds: string[]) => void;
  onCustomersChange?: (customers: ExistingCustomer[]) => void;
  campaigns?: ExistingEmailCampaign[];
}

/**
 * Props for the CampaignSelectionDialog component
 */
interface CampaignSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  campaigns: ExistingEmailCampaign[];
  selectedCampaignIds: string[];
  onSelectionChange: (campaignIds: string[]) => void;
}

function CampaignSelectionDialog({
  isOpen,
  onClose,
  campaigns,
  selectedCampaignIds,
  onSelectionChange,
}: CampaignSelectionDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelection, setLocalSelection] =
    useState<string[]>(selectedCampaignIds);

  useEffect(() => {
    setLocalSelection(selectedCampaignIds);
  }, [selectedCampaignIds]);

  const filteredCampaigns = useMemo(() => {
    if (!searchTerm) return campaigns;
    const search = searchTerm.toLowerCase();
    return campaigns.filter(
      (campaign) =>
        campaign.campaignName.toLowerCase().includes(search) ||
        campaign.description?.toLowerCase().includes(search),
    );
  }, [campaigns, searchTerm]);

  const handleToggleCampaign = (campaignId: string) => {
    setLocalSelection((prev) =>
      prev.includes(campaignId)
        ? prev.filter((id) => id !== campaignId)
        : [...prev, campaignId],
    );
  };

  const handleSelectAll = () => {
    setLocalSelection(filteredCampaigns.map((c) => c.id));
  };

  const handleClearAll = () => {
    setLocalSelection([]);
  };

  const handleApply = () => {
    onSelectionChange(localSelection);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelection(selectedCampaignIds);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="mb-4 text-lg font-medium leading-6 text-gray-900"
                >
                  Exclude Customers from Previous Campaigns
                </Dialog.Title>

                <div className="mb-4">
                  <p className="mb-4 text-sm text-gray-600">
                    Select campaigns to exclude their recipients from this new
                    campaign.
                  </p>

                  {/* Search */}
                  <div className="relative mb-4">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search campaigns..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Selection Controls */}
                  <div className="mb-4 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      {localSelection.length} of {filteredCampaigns.length}{" "}
                      campaigns selected
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSelectAll}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Select All
                      </button>
                      <button
                        onClick={handleClearAll}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  {/* Campaign List */}
                  <div className="max-h-96 overflow-y-auto rounded-md border border-gray-200">
                    {filteredCampaigns.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        {searchTerm
                          ? "No campaigns match your search."
                          : "No campaigns available."}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {filteredCampaigns.map((campaign) => (
                          <div
                            key={campaign.id}
                            className="flex items-center p-4 hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={localSelection.includes(campaign.id)}
                              onChange={() => handleToggleCampaign(campaign.id)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="ml-3 min-w-0 flex-1">
                              <div className="truncate text-sm font-medium text-gray-900">
                                {campaign.campaignName}
                              </div>
                              {campaign.description && (
                                <div className="truncate text-sm text-gray-500">
                                  {campaign.description}
                                </div>
                              )}
                              <div className="mt-1 text-xs text-gray-400">
                                {campaign.totalRecipients} recipients •{" "}
                                {campaign.timestampCreated
                                  ?.toDate()
                                  .toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <BaseButtonSecondary onClick={handleCancel}>
                    Cancel
                  </BaseButtonSecondary>
                  <BaseButtonPrimary onClick={handleApply}>
                    Apply Selection
                  </BaseButtonPrimary>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

/**
 * Smart Customer Selector - Advanced customer selection with server-side filtering
 *
 * This component provides an intelligent customer selection interface that:
 * - Uses server-side filtering with optimized Firestore queries
 * - Provides real-time estimated counts without expensive operations
 * - Only queries when user explicitly searches
 * - Shows linear progress with detailed feedback
 * - Automatically stops fetching when campaign limits are reached
 * - Handles 100,000+ customers efficiently without browser performance issues
 *
 * @param props - Component props
 * @param props.siteKey - The site key for database queries
 * @param props.selectedCustomerIds - Currently selected customer IDs
 * @param props.onSelectionChange - Callback when selection changes
 * @param props.onCustomersChange - Callback for customer objects
 * @param props.campaigns - Available email campaigns for exclusion filtering
 * @returns JSX.Element
 */
export function SmartCustomerSelector({
  siteKey,
  selectedCustomerIds,
  onSelectionChange,
  onCustomersChange,
  campaigns = [],
}: SmartCustomerSelectorProps) {
  // Filter state
  const [customerTypeFilter, setCustomerTypeFilter] = useState<
    "all" | "residential" | "commercial"
  >("all");
  const [excludedCampaignIds, setExcludedCampaignIds] = useState<string[]>([]);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);

  // Search state
  const [customers, setCustomers] = useState<ExistingCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [estimatedCount, setEstimatedCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [estimatingCount, setEstimatingCount] = useState(false);
  const [fetchProgress, setFetchProgress] = useState({ fetched: 0, total: 0 });

  // Calculate customers with email from current results for progress display
  const customersWithEmailCount = useMemo(() => {
    return customers.filter(
      (customer) => customer.email && customer.email.length > 0,
    ).length;
  }, [customers]);

  // Build active filters from current state
  const activeFilters = useMemo(() => {
    const filters: CustomerFilter[] = [];

    // Always include email requirement for email campaigns
    filters.push({
      type: "hasEmail",
      value: true,
      label: "Has Email Address",
    });

    if (customerTypeFilter === "residential") {
      filters.push({
        type: "customerType",
        value: "residential",
        label: "Residential Only",
      });
    } else if (customerTypeFilter === "commercial") {
      filters.push({
        type: "customerType",
        value: "commercial",
        label: "Commercial Only",
      });
    }

    if (excludedCampaignIds.length > 0) {
      filters.push({
        type: "excludeCampaigns",
        value: excludedCampaignIds,
        label: `Exclude ${excludedCampaignIds.length} campaign${excludedCampaignIds.length > 1 ? "s" : ""}`,
      });
    }

    return filters;
  }, [customerTypeFilter, excludedCampaignIds]);

  // Only update estimated count when filters change, but don't search
  useEffect(() => {
    const updateEstimate = async () => {
      if (customerTypeFilter === "all" && excludedCampaignIds.length === 0) {
        setEstimatedCount(0);
        setEstimatingCount(false);
        return;
      }

      try {
        setEstimatingCount(true);
        const excludeIds =
          excludedCampaignIds.length > 0
            ? await getCustomerIdsFromCampaigns(siteKey, excludedCampaignIds)
            : [];

        const count = await getCustomerCountWithFilters(
          siteKey,
          activeFilters,
          excludeIds,
        );
        setEstimatedCount(count);
      } catch (err) {
        console.error("Error estimating count:", err);
        setEstimatedCount(0);
      } finally {
        setEstimatingCount(false);
      }
    };

    updateEstimate();
  }, [activeFilters, siteKey, excludedCampaignIds, customerTypeFilter]);

  const handleSearch = async () => {
    if (customerTypeFilter === "all" && excludedCampaignIds.length === 0) {
      setError(
        "Please select a customer type filter or campaign exclusions before searching.",
      );
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);
    setFetchProgress({ fetched: 0, total: estimatedCount });

    try {
      const excludeIds =
        excludedCampaignIds.length > 0
          ? await getCustomerIdsFromCampaigns(siteKey, excludedCampaignIds)
          : [];

      const result = await queryCustomersWithFilters({
        siteKey,
        filters: activeFilters,
        pageSize: 1000,
        excludeCustomerIds: excludeIds,
        emailLimit: EMAIL_CAMPAIGN_LIMITS.MAX_RECIPIENTS, // Stop fetching once we have enough customers with emails
        onProgress: (fetched, total) => {
          setFetchProgress({ fetched, total: total || estimatedCount });
        },
      });

      setCustomers(result.customers);

      // Auto-select all customers with email addresses if the search was successful
      const customersWithEmail = result.customers.filter(
        (customer) => customer.email && customer.email.length > 0,
      );
      onSelectionChange(customersWithEmail.map((c) => c.id));
      onCustomersChange?.(result.customers);
    } catch (err) {
      console.error("Error searching customers:", err);
      setError("Failed to search customers. Please try again.");
      setCustomers([]);
    } finally {
      setLoading(false);
      setFetchProgress({ fetched: 0, total: 0 });
    }
  };

  const handleClearFilters = () => {
    setCustomerTypeFilter("all");
    setExcludedCampaignIds([]);
    setCustomers([]);
    setError(null);
    setEstimatedCount(0);
    setHasSearched(false);
    setFetchProgress({ fetched: 0, total: 0 });
    onSelectionChange([]);
    onCustomersChange?.([]);
  };

  const handleRemoveFilter = (filterType: string) => {
    switch (filterType) {
      case "customerType":
        setCustomerTypeFilter("all");
        break;
      case "excludeCampaigns":
        setExcludedCampaignIds([]);
        break;
      // Note: Can't remove hasEmail filter since it's always required
    }
  };

  const selectedCount = selectedCustomerIds.length;
  const remainingEmails = EMAIL_CAMPAIGN_LIMITS.MAX_RECIPIENTS - selectedCount;
  const isNearLimit = remainingEmails < 100;
  const isOverLimit = selectedCount > EMAIL_CAMPAIGN_LIMITS.MAX_RECIPIENTS;

  return (
    <div className="space-y-6">
      {/* Email Limit Warning */}
      <div
        className={`rounded-lg border p-4 ${
          isOverLimit
            ? "border-red-200 bg-red-50"
            : isNearLimit
              ? "border-yellow-200 bg-yellow-50"
              : "border-blue-200 bg-blue-50"
        }`}
      >
        <div className="flex items-center gap-2">
          {(isOverLimit || isNearLimit) && (
            <ExclamationTriangleIcon
              className={`h-5 w-5 ${
                isOverLimit ? "text-red-600" : "text-yellow-600"
              }`}
            />
          )}
          <div>
            <div className="text-sm font-medium">
              Email Campaign Limit:{" "}
              {EMAIL_CAMPAIGN_LIMITS.MAX_RECIPIENTS.toLocaleString()} emails
            </div>
            <div
              className={`text-sm ${
                isOverLimit
                  ? "text-red-600"
                  : isNearLimit
                    ? "text-yellow-600"
                    : "text-gray-600"
              }`}
            >
              {isOverLimit
                ? `Over limit by ${(selectedCount - EMAIL_CAMPAIGN_LIMITS.MAX_RECIPIENTS).toLocaleString()} emails. Please remove some customers.`
                : `${selectedCount.toLocaleString()} selected • ${remainingEmails.toLocaleString()} remaining`}
            </div>
          </div>
        </div>
      </div>

      {/* Filter Builder */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">
            Customer Filters
          </h3>
        </div>

        <div className="space-y-6">
          {/* Customer Type */}
          <div>
            <label className="mb-3 block text-sm font-medium text-gray-700">
              Customer Type
            </label>
            <select
              value={customerTypeFilter}
              onChange={(e) =>
                setCustomerTypeFilter(
                  e.target.value as "all" | "residential" | "commercial",
                )
              }
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">All Customers</option>
              <option value="residential">Residential Only</option>
              <option value="commercial">Commercial Only</option>
            </select>
          </div>

          {/* Campaign Exclusion */}
          <div>
            <label className="mb-3 block text-sm font-medium text-gray-700">
              Exclude customers from previous campaigns
            </label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowCampaignDialog(true)}
                className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <CalendarIcon className="h-4 w-4" />
                {excludedCampaignIds.length === 0
                  ? "Select Campaigns to Exclude"
                  : `${excludedCampaignIds.length} Campaign${excludedCampaignIds.length > 1 ? "s" : ""} Selected`}
              </button>
              {excludedCampaignIds.length > 0 && (
                <button
                  onClick={() => setExcludedCampaignIds([])}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Note about email requirement */}
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
            <div className="text-sm text-blue-700">
              <strong>Note:</strong> Only customers with email addresses will be
              included.
            </div>
          </div>

          {/* Active Filters Display */}
          {(customerTypeFilter !== "all" || excludedCampaignIds.length > 0) && (
            <div>
              <label className="mb-3 block text-sm font-medium text-gray-700">
                Active Filters
              </label>
              <div className="flex flex-wrap gap-2">
                {activeFilters
                  .filter((filter) => filter.type !== "hasEmail")
                  .map((filter, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-800"
                    >
                      {filter.label}
                      <button
                        onClick={() => handleRemoveFilter(filter.type)}
                        className="ml-1 hover:text-blue-600"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
              </div>
            </div>
          )}

          {/* Estimated Count */}
          {(customerTypeFilter !== "all" || excludedCampaignIds.length > 0) && (
            <div className="rounded-md bg-gray-50 p-3">
              <div className="text-sm text-gray-600">
                {estimatingCount ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
                    Estimating results...
                  </div>
                ) : (
                  <>
                    Estimated results:{" "}
                    <span className="font-medium text-gray-900">
                      {estimatedCount.toLocaleString()}
                    </span>{" "}
                    customers with email addresses
                  </>
                )}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <div className="text-sm text-red-600">{error}</div>
            </div>
          )}

          {/* Search Button */}
          <div className="flex gap-3">
            <BaseButtonPrimary
              onClick={handleSearch}
              disabled={
                loading ||
                (customerTypeFilter === "all" &&
                  excludedCampaignIds.length === 0)
              }
              className="flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  Searching...
                </>
              ) : (
                <>
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  Search Customers
                </>
              )}
            </BaseButtonPrimary>

            {(customerTypeFilter !== "all" ||
              excludedCampaignIds.length > 0 ||
              hasSearched) && (
              <BaseButtonSecondary onClick={handleClearFilters}>
                Clear All Filters
              </BaseButtonSecondary>
            )}
          </div>
        </div>
      </div>

      {/* Search Results */}
      {hasSearched && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Search Results
            </h3>
            <div className="text-sm text-gray-600">
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
                  Loading customers...
                </div>
              ) : (
                `${customers.length.toLocaleString()} customers found`
              )}
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
              <div className="mt-4 text-sm text-gray-600">
                <div>Searching customers...</div>
                <div className="mt-1 text-xs text-gray-500">
                  This may take a moment for large customer lists
                </div>

                {/* Progress Bar */}
                {fetchProgress.total > 0 && (
                  <div className="mx-auto mt-4 max-w-md">
                    <div className="mb-1 flex justify-between text-xs text-gray-500">
                      <span>
                        {fetchProgress.fetched.toLocaleString()} customers
                        fetched
                      </span>
                      <span>
                        {fetchProgress.total.toLocaleString()} estimated
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-200">
                      <div
                        className="h-2 rounded-full bg-blue-600 transition-all duration-300 ease-out"
                        style={{
                          width: `${Math.min(100, (fetchProgress.fetched / fetchProgress.total) * 100)}%`,
                        }}
                      ></div>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {fetchProgress.total > 0
                        ? `${Math.round((fetchProgress.fetched / fetchProgress.total) * 100)}% complete`
                        : "Starting..."}
                    </div>
                    {/* Show email count during loading */}
                    {customersWithEmailCount > 0 && (
                      <div className="mt-1 text-xs text-blue-600">
                        {customersWithEmailCount.toLocaleString()} customers
                        with email addresses found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : customers.length === 0 ? (
            <div className="py-8 text-center">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h4 className="mt-2 text-sm font-medium text-gray-900">
                No customers found
              </h4>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your filters to find more customers.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Selection Summary */}
              <div className="rounded-md bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {selectedCount.toLocaleString()} of{" "}
                      {customers.length.toLocaleString()} customers selected
                    </div>
                    <div className="text-sm text-gray-600">
                      Ready to receive email campaign
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const customersWithEmail = customers.filter(
                          (customer) =>
                            customer.email && customer.email.length > 0,
                        );
                        onSelectionChange(customersWithEmail.map((c) => c.id));
                        onCustomersChange?.(customersWithEmail);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => onSelectionChange([])}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              </div>

              {/* Customer List */}
              <div className="max-h-96 overflow-y-auto rounded-md border border-gray-200">
                <div className="divide-y divide-gray-200">
                  {customers.map((customer) => {
                    const isSelected = selectedCustomerIds.includes(
                      customer.id,
                    );
                    const hasEmail =
                      customer.email && customer.email.length > 0;
                    const displayName =
                      customer.type === "commercial"
                        ? customer.name
                        : `${customer.firstName || ""} ${customer.lastName || ""}`.trim();

                    return (
                      <div
                        key={customer.id}
                        className={`flex items-center p-4 hover:bg-gray-50 ${
                          isSelected ? "bg-blue-50" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const newSelectedIds = [
                                ...selectedCustomerIds,
                                customer.id,
                              ];
                              onSelectionChange(newSelectedIds);
                              // Update selected customers
                              const selectedCustomers = customers.filter((c) =>
                                newSelectedIds.includes(c.id),
                              );
                              onCustomersChange?.(selectedCustomers);
                            } else {
                              const newSelectedIds = selectedCustomerIds.filter(
                                (id) => id !== customer.id,
                              );
                              onSelectionChange(newSelectedIds);
                              // Update selected customers
                              const selectedCustomers = customers.filter((c) =>
                                newSelectedIds.includes(c.id),
                              );
                              onCustomersChange?.(selectedCustomers);
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="ml-3 min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="truncate text-sm font-medium text-gray-900">
                              {displayName}
                            </div>
                            <span
                              className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                                customer.type === "commercial"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {customer.type === "commercial"
                                ? "Commercial"
                                : "Residential"}
                            </span>
                            {!hasEmail && (
                              <span className="inline-flex items-center rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                                No Email
                              </span>
                            )}
                          </div>
                          {hasEmail && (
                            <div className="truncate text-sm text-gray-500">
                              {customer.email?.[0]}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Campaign Selection Dialog */}
      <CampaignSelectionDialog
        isOpen={showCampaignDialog}
        onClose={() => setShowCampaignDialog(false)}
        campaigns={campaigns}
        selectedCampaignIds={excludedCampaignIds}
        onSelectionChange={setExcludedCampaignIds}
      />
    </div>
  );
}
